import axios from 'axios';
import Cookies from 'js-cookie';

const state = {
  generatedWorker: null,
  availableWorkers: []
};

const mutations = {
  SET_GENERATED_WORKER(state, worker) {
    state.generatedWorker = worker;
  },
  SET_AVAILABLE_WORKERS(state, workers) {
    state.availableWorkers = workers;
  }
};

const actions = {
  async generateWorker({ commit, rootState, dispatch }, { work }) {
    try {
      if (!rootState.player.playerData) {
        await dispatch('player/loadPlayerData', null, { root: true });
      }

      if (!rootState.player.playerData?.id) {
        throw new Error('Player not found');
      }

      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/workers/generate`,
        {
          playerId: rootState.player.playerData.id,
          work
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );

      if (response.data?.worker) {
        await dispatch('player/loadPlayerData', null, { root: true });
        commit('SET_GENERATED_WORKER', response.data.worker);
        return response.data.worker;
      }
    } catch (error) {
      console.error('Generate worker error:', error);
      throw error;
    }
  },

  async hireWorker({ commit, dispatch, rootState }, workerData) {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/workers/hire`,
        {
          ...workerData,
          playerId: rootState.player.playerData.id
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );

      if (response.data?.worker) {
        commit('SET_GENERATED_WORKER', null);
        await dispatch('player/loadPlayerData', null, { root: true });
        return response.data.worker;
      }
    } catch (error) {
      console.error('Hire worker error:', error);
      throw error;
    }
  },

  async checkWorkersRestingStatus({ dispatch }) {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/resting/check`,
        {},
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      if (response.data?.data?.processed?.length > 0) {
        // If any workers were processed, refresh player data
        await dispatch('player/loadPlayerData', null, { root: true });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking workers resting status:', error);
      // Don't throw the error - just log it and continue
    }
  },
  
  async fetchAvailableWorkers({ commit, rootState, dispatch }) {
    try {
      // First check if any workers have completed resting
      await dispatch('checkWorkersRestingStatus');
      
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/workers/player/${rootState.player.playerData.id}?excludeBusinessWorkers=true`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      commit('SET_AVAILABLE_WORKERS', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching available workers:', error);
      throw error;
    }
  },
  
  async consumeFood(_, { workerId, foodId, amount = 1 }) {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/workers/consume-food`,
        {
          workerId,
          foodId,
          amount
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      if (response.data?.success) {
        return response.data.worker;
      }
    } catch (error) {
      console.error('Error consuming food for worker:', error);
      throw error;
    }
  },

  async refreshWorker({ dispatch }, workerId) {
    try {
      // Get fresh worker data
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/workers/${workerId}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      if (response.data?.data) {
        // If the worker is part of the player's data, refresh player data
        await dispatch('player/loadPlayerData', null, { root: true });
        return response.data.data;
      }
    } catch (error) {
      console.error('Error refreshing worker data:', error);
      throw error;
    }
  },

  async sellWorker({ dispatch, rootState }, { workerId, price, priceInParalleliums }) {
    try {
      // Ensure player data is loaded
      let playerData = rootState.player.playerData;
      if (!playerData) {
        await dispatch('player/loadPlayerData', null, { root: true });
        playerData = rootState.player.playerData;
      }

      // Double check after loading
      if (!playerData?.id) {
        throw new Error('Unable to find player data. Please try logging out and back in.');
      }

      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/worker-stores/sell`,
        {
          workerId,
          price,
          priceInParalleliums: priceInParalleliums || false
        },
        {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        await dispatch('fetchAvailableWorkers');
        await dispatch('player/loadPlayerData', null, { root: true });
        return response.data;
      }
    } catch (error) {
      console.error('Error selling worker:', error.response?.data || error);
      // Throw a more user-friendly error message
      const errorMsg = error.response?.data?.error?.message || error.message;
      throw new Error(`Failed to sell worker: ${errorMsg}`);
    }
  }
};

const getters = {
  generatedWorker: state => state.generatedWorker,
  availableWorkers: state => state.availableWorkers
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
